"use client";

import { useEffect, useMemo, useState } from "react";
import { useExperiment } from "@/hooks/useExperiment";
import { useCrossSellDevPreview } from "@/contexts/CrossSellDevPreviewContext";
import {
  CROSS_SELL_V2_TIER1_COUNTRIES,
  LOOKSMAX_AVATAR_GRID_LOCKED_VARIANT,
  getLooksmaxCopy,
  isLooksmaxCopyVariantId,
  type CrossSellOfferCopy,
  type CrossSellOfferId,
  type LooksmaxCopyVariantId,
} from "@/libs/cross-sell-v2";
import { LOOKSMAX_COPY_V1_EXPERIMENT_ID } from "@/libs/experiments";
import { readClientCfCountry } from "@/libs/attractiveness-geo";

function isTier1Eligible(country: string | null): boolean {
  if (!country) return true;
  return CROSS_SELL_V2_TIER1_COUNTRIES.has(country);
}

/**
 * Resolves looksmax cross-sell offers for sitewide banner vs avatar grid.
 *
 * - Sitewide: A/B via looksmax_copy_v1 when active; otherwise PromoBanner deal default.
 * - Avatar grid: locked to LOOKSMAX_AVATAR_GRID_LOCKED_VARIANT when experiment + ENABLE_* flags on.
 * - localhost dev switcher overrides sitewide copy only; avatar stays locked.
 * `ready` is false until client mount — avoids SSR/client visitorId mismatch.
 */
export function useCrossSellOffer() {
  const [ready, setReady] = useState(false);
  const { isDev, devVariantId } = useCrossSellDevPreview();
  const { variant, isActive } = useExperiment(LOOKSMAX_COPY_V1_EXPERIMENT_ID);

  useEffect(() => {
    setReady(true);
  }, []);

  const eligible = useMemo(() => {
    if (!ready) return false;
    if (isDev) return !!devVariantId;
    if (!isActive || !isLooksmaxCopyVariantId(variant)) return false;
    if (!isTier1Eligible(readClientCfCountry())) return false;
    return true;
  }, [ready, isDev, devVariantId, isActive, variant]);

  const copyVariantId = useMemo((): LooksmaxCopyVariantId | null => {
    if (!eligible) return null;
    if (isDev) return devVariantId;
    if (!isLooksmaxCopyVariantId(variant)) return null;
    return variant;
  }, [eligible, isDev, devVariantId, variant]);

  const offer: CrossSellOfferCopy | null = copyVariantId ? getLooksmaxCopy(copyVariantId) : null;
  const avatarOffer: CrossSellOfferCopy | null = eligible
    ? getLooksmaxCopy(LOOKSMAX_AVATAR_GRID_LOCKED_VARIANT)
    : null;

  return {
    offer,
    offerId: (offer ? "looksmax" : null) as CrossSellOfferId | null,
    avatarOffer,
    avatarOfferId: (avatarOffer ? "looksmax" : null) as CrossSellOfferId | null,
    copyVariantId,
    ready,
    isExperimentActive: isActive,
    isDevOverride: isDev,
  };
}
